// apps/client/src/ui/screens/news.tsx (M1-T17) — the town news board (the plaza board and the Menu): a dot in the friend's colour,
// the text and a relative time per row (DESIGN.md card "news" variant). Opening it marks the newest item seen (the board glow stops).
import { friendColors, type FriendColor } from '@whiskerton/content';
import type { NewsItem } from '@whiskerton/shared';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { peekGame, useCommands } from '../../game/instance';
import { renderNews } from '../../state/slices/mail';
import { useGame } from '../../state/store';
import { useT } from '../i18n';
import { Button, Panel } from '../kit';
import type { ScreenDef } from '../router';

declare module '../router' { interface ScreenParams { news: undefined } }

/** A relative time ("just now", "5 min ago", "3 days ago"); the mailbox rows use it too. `at` is town time, so it is measured from the
 *  town clock (server time plus any dev offset), never the device clock (spec §5). */
export function Ago({ at }: { at: number }) {
  const t = useT();
  const now = peekGame()?.now() ?? Date.now();
  const min = Math.max(0, Math.round((now - at) / 60_000));
  return <>{min < 1 ? t('news.justNow') : min < 60 ? t('news.minutesAgo', { n: min }) : min < 1440 ? t('news.hoursAgo', { n: Math.round(min / 60) }) : t('news.daysAgo', { n: Math.round(min / 1440) })}</>;
}
export function NewsRow({ item }: { item: NewsItem }) {
  const colour = item.actor.color ? friendColors[item.actor.color as FriendColor].tag : null;
  return (
    <li className="news-row" style={{ display: 'grid', gridTemplateColumns: '14px 1fr auto', alignItems: 'baseline', gap: 10, padding: '8px 0', borderBottom: '1.5px solid var(--cream-200)' }}>
      {colour ? <span className="news-dot" style={{ background: colour, width: 12, height: 12, borderRadius: '50%', display: 'inline-block' }} /> : <span />}
      <span className="news-text" style={{ fontWeight: 800 }}>{renderNews(item)}</span>
      <time className="news-time" dateTime={new Date(item.at).toISOString()} style={{ color: 'var(--wood-600)', fontSize: 14, whiteSpace: 'nowrap' }}><Ago at={item.at} /></time>
    </li>
  );
}

function News() {
  const t = useT();
  const cmd = useCommands();
  const { items, more } = useGame(useShallow((s) => ({ items: s.mailNews, more: s.mailNewsMore })));
  const [busy, setBusy] = useState(false);
  const load = async (before: number | null) => {
    setBusy(true);
    try {
      const res = await cmd.act('news.list', { before });
      if (!res.ok) return;
      const s = useGame.getState();
      s.mailSetNews(res.r.items, res.r.more, before !== null);
      const newest = Math.max(s.mailNewsHead, res.r.items[0]?.id ?? 0);
      if (before === null && newest > s.mailNewsSeen) s.mailMarkNewsSeen(newest);
    } finally { setBusy(false); }
  };
  useEffect(() => { void load(null); }, []);   // once on open
  return (
    <Panel title={t('news.title')}>
      <div style={{ width: 'min(560px, 72cqw)', maxWidth: '100%', display: 'grid', gap: 12 }}>
        {items.length === 0 && !busy && <p style={{ margin: 0 }}>{t('news.empty')}</p>}
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{items.map((i) => <NewsRow key={i.id} item={i} />)}</ul>
        {more && <Button variant="quiet" disabled={busy} onClick={() => void load(items.at(-1)?.id ?? null)}>{t('news.more')}</Button>}
      </div>
    </Panel>
  );
}
export const screen: ScreenDef<'news'> = { id: 'news', layer: 'panel', menu: { label: 'news.title', order: 12 }, component: () => <News /> };
